import { EmptyState, FlexBox, Page } from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { Fragment, memo, useCallback, useEffect, useRef } from 'react';
import { useMount, useUnmount } from 'react-use';

import { dashboardApi } from 'apis/dashboard';
import { websocketActions } from 'app/middleware/websocket/actions/websocket';
import { useDashboardRefetchEffect } from 'utils/dashboard';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';
import { useServerStorage } from 'utils/useServerStorage';

import { DashboardError } from '../../components/error/dashboardError';
import { DashboardHeader } from '../../components/header/dashboardHeader';
import { dashboardActions } from '../../dashboard.actions';
import rootStyles from '../../dashboard.view.module.scss';
import { QueueDashboardLoading } from '../queue/components/loading/queueDashboardLoading';
import { QueueDashboardTable } from '../queue/components/queues/components/table/queueDashboardTable';

import { FavoriteDashboardControls } from './components/controls/favoriteDashboardControls';
import { FavoriteDashboardFavorite } from './components/favorite/favoriteDashboardFavorite';

export const FavoriteDashboardView = memo(
  function FavoriteDashboardView(): JSX.Element {
    const dispatch = useAppDispatch();
    const navigate = useAppNavigate();
    const contentRef = useRef<HTMLDivElement>(null);
    const storage = useServerStorage('user');
    const { params } = useAppSearchParams('/dashboard/favorites');
    const { layout } = storage.data.dashboard ?? {};

    const [
      readDashboardFavorites,
      { favorites, isUninitialized, isFetching, isError },
    ] = dashboardApi.useLazyReadDashboardFavoritesQuery({
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        favorites:
          data?.data.favorites?.filter(
            (favorite) => favorite.params.queue_ids.length,
          ) ?? [],
      }),
    });

    const refetch = useCallback(() => {
      readDashboardFavorites({
        dashboard: 'queue',
        sort_by: params.sort_by,
      });
    }, [params.sort_by, readDashboardFavorites]);

    // Fetches the favorites on mount and whenever the sort_by param changes.
    useEffect(refetch, [refetch]);

    // Refetches the favorites whenever the user triggers it with the "Refresh data" button.
    useDashboardRefetchEffect({ refetch });

    useMount(() => {
      ampli.dashboardViewed({ version: 'favorites' });

      dispatch(
        websocketActions.request({
          type: 'connection:subscribe',
          params: {
            type: 'real_time_dashboard',
            dashboard_type: 'queue',
          },
        }),
      );
    });

    useUnmount(() => {
      dispatch(
        websocketActions.request({
          type: 'connection:unsubscribe',
          params: {
            type: 'real_time_dashboard',
            dashboard_type: 'queue',
          },
        }),
      );

      dispatch(
        dashboardActions.setLastVisitedUrl({
          path: '/dashboard/favorites',
          params: {
            search: params,
          },
        }),
      );
    });

    const renderContent = (): JSX.Element => {
      if (isUninitialized || isFetching) {
        return (
          <QueueDashboardLoading
            contentHeight={contentRef.current?.clientHeight}
          />
        );
      }

      if (isError) {
        return <DashboardError />;
      }

      if (favorites.length === 0) {
        return (
          <FlexBox
            flexDirection="column"
            height="100%"
            alignItems="center"
            justifyContent="center"
          >
            <EmptyState
              title="You do not have any favorites."
              description="Click on the star icon next to a queue to add it to your favorites."
              icon="star"
              buttonProps={{
                children: 'See assigned queues',
                onClick: () => {
                  navigate({
                    path: '/dashboard/queues',
                    params: {
                      search: {
                        type: 'agent',
                      },
                    },
                  });
                },
              }}
            />
          </FlexBox>
        );
      }

      const Root = layout === 'compact' ? QueueDashboardTable : Fragment;

      return (
        <Root>
          {favorites.map((favorite) => (
            <FavoriteDashboardFavorite
              key={favorite.id}
              favorite={favorite}
              highlighted={false}
            />
          ))}
        </Root>
      );
    };

    return (
      <Page>
        <Page.Head
          className={rootStyles.pageHead}
          title={<DashboardHeader dashboard="favorite" />}
          controls={<FavoriteDashboardControls />}
        />

        <Page.Body ref={contentRef}>
          <Page.Section>
            <FlexBox flexDirection="column" gap="300" height="100%">
              {renderContent()}
            </FlexBox>
          </Page.Section>
        </Page.Body>
      </Page>
    );
  },
);
